import React from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import PageMetadata from "../../components/PageMetadata"
import PageHero from "../../components/PageHero"
import Card from "../../components/Card"
import Emoji from "../../components/Emoji"
import GhostCard from "../../components/GhostCard"
import CalloutBanner from "../../components/CalloutBanner"
import ButtonLink from "../../components/ButtonLink"
import Breadcrumbs from "../../components/Breadcrumbs"
import {
  CardContainer,
  Content,
  Page,
  Divider,
} from "../../components/SharedStyledComponents"

const H2 = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin-top: 2rem;
  margin-bottom: 1.5rem;
`

const Text = styled.p`
  font-size: 1.15rem;
  line-height: 1.6;
  color: ${(props) => props.theme.colors.text200};
  max-width: 800px;
  margin-bottom: 2rem;
`

const StyledCard = styled(Card)`
  flex: 1 1 30%;
  min-width: 240px;
  margin: 1rem;
  padding: 1.5rem;
  box-shadow: ${(props) => props.theme.colors.tableBoxShadow};
  border: 1px solid ${(props) => props.theme.colors.lightBorder};
  border-radius: 8px;
  @media (max-width: ${(props) => props.theme.breakpoints.l}) {
    flex: 1 1 30%;
  }
`

const GridContainer = styled.div`
  margin-top: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`

const StyledGhostCard = styled(GhostCard)`
  padding: 1.5rem;
  border-radius: 8px;
  background: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.border};
`

const HighlightText = styled.span`
  font-weight: bold;
  color: ${(props) => props.theme.colors.primary};
`

const StyledCallout = styled(CalloutBanner)`
  margin: 4rem 0;
  .gatsby-image-wrapper {
    border-radius: 12px;
  }
`

const MetricsContainer = styled.div`
  margin-top: 3rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-bottom: 4rem;
  @media (max-width: ${(props) => props.theme.breakpoints.l}) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: ${(props) => props.theme.breakpoints.s}) {
    grid-template-columns: 1fr;
  }
`

const MetricBox = styled.div`
  background: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.lightBorder};
  box-shadow: ${(props) => props.theme.colors.tableBoxShadow};
  padding: 2rem;
  border-radius: 8px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const MetricNumber = styled.div`
  font-size: 36px;
  font-weight: 800;
  color: ${(props) => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`

const MetricLabel = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: 0.25rem;
`

const MetricDesc = styled.div`
  font-size: 0.85rem;
  color: ${(props) => props.theme.colors.text200};
`

const NewProjectAIPage = ({ data, location }) => {
  const heroContent = {
    title: "ALIADO ESTRATÉGICO",
    header:
      "New Project AI: Consultoría Estratégica, PMO y Tecnología a tu Medida",
    subtitle:
      "New Project AI es una firma de consultoría con sede en Medellín, Colombia, especializada en conectar la tecnología con los resultados de negocio mediante la perfecta integración de personas, procesos, gestión de calidad y metodologías de clase mundial.",
    image: data.business.childImageSharp.fluid,
    alt: "New Project AI Business Strategy",
    buttons: [
      {
        path: "https://www.newprojectai.com/",
        content: "Visitar Sitio Web Oficial",
        isExternal: true,
      },
    ],
  }

  const services = [
    {
      emoji: ":bar_chart:",
      title: "Oficina de Proyectos (PMO)",
      description:
        "Gobernanza completa, planificación minuciosa y control de portafolios altamente estratégicos. Gestión de riesgos y definición de KPIs clave.",
    },
    {
      emoji: ":gear:",
      title: "Gestión de Proyectos SAP",
      description:
        "Liderazgo y acompañamiento experto en implementaciones y migraciones complejas (SAP S/4HANA) bajo el marco metodológico SAP Activate.",
    },
    {
      emoji: ":rocket:",
      title: "Agile Delivery & Coaching",
      description:
        "Aceleración de la entrega de valor real mediante metodologías ágiles adaptadas al entorno y la cultura de tu organización.",
    },
    {
      emoji: ":white_check_mark:",
      title: "QA Testing & Automatización",
      description:
        "Pruebas funcionales de extremo a extremo, automatización robusta y completa trazabilidad para garantizar la máxima calidad técnica del software.",
    },
    {
      emoji: ":shield:",
      title: "Ciberseguridad",
      description:
        "Estrategias y soluciones avanzadas de seguridad informática para blindar la operación, proteger activos digitales e infraestructura.",
    },
    {
      emoji: ":chart_with_upwards_trend:",
      title: "Optimización y Procesos",
      description:
        "Reducción drástica del time-to-market y optimización de flujos de trabajo utilizando prácticas como Lean Six Sigma y automatización.",
    },
  ]

  const orbitSteps = [
    {
      step: "01",
      name: "DISCOVER",
      description:
        "Entendimiento a fondo de los objetivos corporativos, desafíos de negocio e identificación de puntos críticos.",
    },
    {
      step: "02",
      name: "DESIGN",
      description:
        "Diseño de la estrategia, definición de la gobernanza, arquitectura del proyecto y marco metodológico ideal.",
    },
    {
      step: "03",
      name: "DELIVER",
      description:
        "Ejecución disciplinada y rigurosa del plan estratégico, control activo de presupuestos, riesgos y desviaciones.",
    },
    {
      step: "04",
      name: "ASSURE",
      description:
        "Control exhaustivo de calidad, pruebas funcionales automatizadas y verificación estricta de cada entregable.",
    },
    {
      step: "05",
      name: "TRANSFORM",
      description:
        "Gestión proactiva del cambio organizacional, transferencia de conocimiento y cultura de mejora continua.",
    },
  ]

  return (
    <Page>
      <PageMetadata
        title="New Project AI - Aliado Estratégico"
        description="Conoce a New Project AI, aliado estratégico de Newinntech en consultoría de alta complejidad, PMO, SAP, metodologías ágiles y calidad de software."
      />
      <PageHero content={heroContent} />
      <Divider />

      <Content>
        <Breadcrumbs slug={location.pathname} />

        <H2>
          Propuesta de Valor de New Project AI{" "}
          <Emoji text=":sparkles:" size={1.2} />
        </H2>
        <Text>
          Muchas organizaciones enfrentan dificultades para traducir sus
          objetivos estratégicos en ejecuciones metodológicas exitosas,
          experimentando desviaciones de presupuesto, desalineación técnica o
          falta de gobierno operativo. New Project AI actúa como un acelerador
          de predictibilidad, liderando iniciativas de alta complejidad técnica
          y organizacional con rigurosidad metodológica.
        </Text>

        <H2>
          Nuestros Servicios Especializados{" "}
          <Emoji text=":hammer_and_wrench:" size={1.2} />
        </H2>
        <CardContainer>
          {services.map((service, idx) => (
            <StyledCard
              key={idx}
              emoji={service.emoji}
              title={service.title}
              description={service.description}
            />
          ))}
        </CardContainer>

        <Divider />

        <H2>
          La Ruta ORBIT: Nuestra Metodología de Ejecución{" "}
          <Emoji text=":compass:" size={1.2} />
        </H2>
        <Text>
          Para asegurar que cada proyecto se entregue dentro de los plazos, el
          presupuesto y con la máxima calidad, implementamos un camino
          predictible y transparente de 5 fases consecutivas:
        </Text>

        <GridContainer>
          {orbitSteps.map((step, idx) => (
            <StyledGhostCard key={idx}>
              <h3>
                <HighlightText>{step.step}.</HighlightText> {step.name}
              </h3>
              <p>{step.description}</p>
            </StyledGhostCard>
          ))}
        </GridContainer>

        <H2>
          Métricas que Respaldan Nuestra Trayectoria{" "}
          <Emoji text=":chart_with_upwards_trend:" size={1.2} />
        </H2>
        <MetricsContainer>
          <MetricBox>
            <MetricNumber>100%</MetricNumber>
            <MetricLabel>Compromiso</MetricLabel>
            <MetricDesc>
              En entrega de alcance, tiempo y presupuesto acordado.
            </MetricDesc>
          </MetricBox>
          <MetricBox>
            <MetricNumber>+45%</MetricNumber>
            <MetricLabel>Eficiencia</MetricLabel>
            <MetricDesc>
              De optimización promedio de procesos mediante Lean Six Sigma.
            </MetricDesc>
          </MetricBox>
          <MetricBox>
            <MetricNumber>98%</MetricNumber>
            <MetricLabel>Garantía</MetricLabel>
            <MetricDesc>
              De excelencia y satisfacción en integraciones técnicas complejas.
            </MetricDesc>
          </MetricBox>
          <MetricBox>
            <MetricNumber>+50</MetricNumber>
            <MetricLabel>Consultores</MetricLabel>
            <MetricDesc>
              Consultores senior certificados en PMI, Scrum, SAFe e ITIL.
            </MetricDesc>
          </MetricBox>
        </MetricsContainer>

        <Divider />

        <StyledCallout
          image={data.consulting.childImageSharp.fluid}
          alt="New Project AI Consulting Team"
          title="¿Buscas garantizar el éxito y control de tus proyectos estratégicos?"
          description="Acelera tus resultados tecnológicos de la mano de New Project AI y Newinntech. Nuestro ecosistema conjunto de ingeniería, agilidad y gobierno asegura que tu inversión de negocio se traduzca en sistemas robustos y confiables."
        >
          <div>
            <ButtonLink to="https://www.newprojectai.com/">
              Visitar Página de New Project AI
            </ButtonLink>
          </div>
        </StyledCallout>
      </Content>
    </Page>
  )
}

export default NewProjectAIPage

export const query = graphql`
  query {
    business: file(relativePath: { eq: "business.png" }) {
      childImageSharp {
        fluid(maxWidth: 800) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    consulting: file(relativePath: { eq: "consulting.png" }) {
      childImageSharp {
        fluid(maxWidth: 800) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
